import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, inject } from '@angular/core';

import { SportsCacheService } from '@frontend/sports/betting-offer/feature/sports-cache';
import { CountItem } from '@frontend/sports/common/core/data-access/sport-model';
import { CompetitionRouteService } from '@frontend/sports/competition-list/feature';
import { CompetitionRoute } from '@frontend/sports/navigation/core/feature/models';
import { CompetitionHeaderPayload } from '@frontend/sports/types/components/widget/types';
import { WidgetComponent } from '@frontend/sports/widget/core/feature';
import { Observable, ReplaySubject, map, of, switchMap, tap } from 'rxjs';

import { PruneHelperService } from '../competition-list/services/prune-helper.service';
import { VirtualCompetitionModel, VirtualCompetitionService } from '../competition-list/services/virtual-competition.service';
import { FixtureListHeaderComponent } from '../fixture-list/fixture-list-header.component';
import { TournamentGroupsComponent } from '../tournament-groups/tournament-groups.component';

type ViewModel = CompetitionHeaderPayload & {
    sport: CountItem;
    isFavouritesEnabled: boolean;
    virtualCompetition: VirtualCompetitionModel | null;
};

@Component({
    selector: 'ms-competition-header-widget',
    templateUrl: './competition-header-widget.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, FixtureListHeaderComponent, TournamentGroupsComponent],
})
export class CompetitionHeaderWidgetComponent extends WidgetComponent<CompetitionHeaderPayload> {
    private competitionRouteService = inject(CompetitionRouteService);
    private virtualCompetitionService = inject(VirtualCompetitionService);
    private pruneService = inject(PruneHelperService);
    private bettingCache = inject(SportsCacheService);

    private readonly payload$ = new ReplaySubject<CompetitionHeaderPayload>(1);
    readonly vm$: Observable<ViewModel | null>;

    @HostBinding('class') className = 'competition-header-widget event-list';

    constructor() {
        super();

        this.vm$ = this.payload$.pipe(
            switchMap((payload) => {
                const route = this.competitionRouteService.params();

                if (!route.sport || !route.league) {
                    return of(null);
                }

                return this.bettingCache.getSport(route.sport).pipe(switchMap((sport) => this.getModel$(sport, route, payload)));
            }),
        );
    }

    private getModel$(sport: CountItem | undefined, route: CompetitionRoute, payload: CompetitionHeaderPayload): Observable<ViewModel | null> {
        const prunnedSportItem = this.pruneService.prune(sport, route);
        const virtualCompetitionItem = this.virtualCompetitionService.getCompetition(sport, route);

        const source$ =
            !prunnedSportItem || !virtualCompetitionItem
                ? of(null)
                : this.virtualCompetitionService.subscribe$(prunnedSportItem, virtualCompetitionItem);

        return source$.pipe(
            map((virtualCompetitionModel) => {
                return {
                    sport: prunnedSportItem!,
                    title: payload.title,
                    // non-virtual competitions and tv2 virtual competitions with one competition assigned, can have favorites enabled
                    isFavouritesEnabled: !virtualCompetitionModel || !!virtualCompetitionModel.realCompetitionId,
                    virtualCompetition: virtualCompetitionModel,
                };
            }),
            tap((_) => this.setWidgetLoaded()),
        );
    }

    protected override onData(payload: CompetitionHeaderPayload): void {
        this.payload$.next(payload);
    }
}
